'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { CHANGELOG_VERSION, CHANGELOG_HEADER, CHANGELOG_ITEMS, CHANGELOG_INTRO, CHANGELOG_STORAGE_KEY } from '@/config/changelog';
import rezervatorLogo from '../public/images/rezervator_green_logo.png';

type ChangelogItemType = 'NOVO' | 'POBOLJŠANJE' | 'POPRAVKA';

const ChangelogModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    // Check if user has seen this version and if pathname contains /dashboard
    if (typeof window !== 'undefined' && pathname?.includes('/dashboard')) {
      const seenVersion = localStorage.getItem(CHANGELOG_STORAGE_KEY);
      if (seenVersion !== CHANGELOG_VERSION) {
        setIsOpen(true);
      }
    }
  }, [pathname]);

  const handleConfirm = () => {
    // Save current version to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(CHANGELOG_STORAGE_KEY, CHANGELOG_VERSION);
      setIsOpen(false);
    }
  };

  const getTypeIcon = (type: ChangelogItemType) => {
    switch (type) {
      case 'NOVO':
        return '✨';
      case 'POPRAVKA':
        return '🔧';
      case 'POBOLJŠANJE':
        return '🚀';
      default:
        return '📄';
    }
  };

  const getTypeBadgeStyles = (type: ChangelogItemType) => {
    switch (type) {
      case 'NOVO':
        return 'bg-green-50 text-green-600';
      case 'POPRAVKA':
        return 'bg-gray-100 text-gray-600';
      case 'POBOLJŠANJE':
        return 'bg-blue/50 text-blue-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop - non-clickable */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-[#88888880] backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative z-10 w-full max-w-2xl mb-8 md:mb-0 mx-4 max-h-[80vh] md:max-h-[85vh] bg-white rounded-lg shadow-xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="px-4 md:px-6 py-4 md:py-5 border-b border-gray-200 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-2 md:gap-6">
                    <div className="flex items-center justify-center flex-shrink-0 mt-2">
                      <Image
                        src={rezervatorLogo}
                        width={94}
                        height={94}
                        alt="Rezervator logo"
                        className="object-contain"
                        priority
                      />
                    </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                      {CHANGELOG_HEADER.title}
                    </h2>
                    <p className="text-xs md:text-sm text-gray-500 mt-1">
                      Verzija {CHANGELOG_HEADER.version} • {CHANGELOG_HEADER.date}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-5 bg-white">
              {/* Intro text */}
              {CHANGELOG_INTRO && (
                <p className="text-gray-700 mb-4 md:mb-6 leading-relaxed text-sm md:text-base">
                  {CHANGELOG_INTRO}
                </p>
              )}
              
              {/* Changelog items - grouped by type */}
              <div className="space-y-6 md:space-y-8">
                {Object.entries(
                  CHANGELOG_ITEMS.reduce((acc, item) => {
                    if (!acc[item.type]) {
                      acc[item.type] = [];
                    }
                    acc[item.type].push(item);
                    return acc;
                  }, {} as Record<ChangelogItemType, Array<{ type: ChangelogItemType; title: string; description: string }>>)
                ).map(([type, items]) => (
                  <div key={type}>
                    <div className="mb-3 md:mb-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 md:px-2.5 py-0.5 md:py-1 rounded-md text-xs font-semibold ${getTypeBadgeStyles(type as ChangelogItemType)}`}>
                        <span className="text-base md:text-lg">{getTypeIcon(type as ChangelogItemType)}</span>
                        {type}
                      </span>
                    </div>
                    <div className="ml-4 md:ml-6 space-y-4 md:space-y-5">
                      {items.map((item, index) => (
                        <div key={index}>
                          <h3 className="font-bold text-gray-900 text-base md:text-lg mb-1 md:mb-1.5">
                            {item.title}
                          </h3>
                          <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 bg-white flex justify-end">
              <button
                onClick={handleConfirm}
                className="bg-primary text-white hover:bg-[#1c1e54] disabled:bg-primary/40 rounded-lg font-semibold tracking-wider transition-all duration-300 ease-linear px-4 py-2.5 md:px-6 md:py-3 text-sm md:text-base w-full md:w-auto"
              >
                Odlično, nastavi s radom
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ChangelogModal;

